import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";

type RecurringInvoiceSchedule = components["schemas"]["RecurringInvoiceScheduleResponse"];
type TaxRate = components["schemas"]["TaxRateResponse"];

// Radix Select item values cannot be empty, so use a sentinel for "no tax rate".
const NONE = "__none__";

interface RemapScheduleTaxRatesDialogProps {
  schedule: RecurringInvoiceSchedule;
  taxRates: TaxRate[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (updated: RecurringInvoiceSchedule) => void;
}

export function RemapScheduleTaxRatesDialog({
  schedule,
  taxRates,
  open,
  onOpenChange,
  onUpdated,
}: RemapScheduleTaxRatesDialogProps) {
  const lineItems = useMemo(() => schedule.lineItems ?? [], [schedule.lineItems]);

  // Map of lineItemId -> selected Select value (a tax rate id, or NONE).
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const knownRateIds = useMemo(() => new Set(taxRates.map((r) => r.id)), [taxRates]);

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, string> = {};
    for (const item of lineItems) {
      if (!item.id) continue;
      initial[item.id] = item.taxRateId ?? NONE;
    }
    setSelections(initial);
    setError(null);
    setSubmitting(false);
  }, [open, lineItems]);

  const changed = useMemo(
    () =>
      lineItems.filter((item) => {
        if (!item.id) return false;
        return selections[item.id] !== (item.taxRateId ?? NONE);
      }),
    [lineItems, selections],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (changed.length === 0) return;
    setError(null);
    setSubmitting(true);

    const { data, error: apiError } = await api.PATCH(
      "/admin/recurring-invoices/{id}/line-item-tax-rates",
      {
        params: { path: { id: schedule.id! } },
        body: {
          lineItems: changed.map((item) => ({
            lineItemId: item.id!,
            taxRateId: selections[item.id!] === NONE ? null : selections[item.id!],
          })),
        },
      },
    );

    if (apiError) {
      setError(apiError.detail || apiError.title || "Failed to update tax rates.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onOpenChange(false);
    if (data) onUpdated(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Remap line-item tax rates</DialogTitle>
            <DialogDescription>
              Point each line item at a valid tax rate (or remove it). The schedule's template is
              re-validated and, if Active, its next run recomputed so it resumes generating.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {lineItems.map((item) => {
              const value = item.id ? (selections[item.id] ?? NONE) : NONE;
              // Surface a current tax rate that no longer exists (the reason a schedule gets stuck)
              // so the admin can still see and replace it.
              const danglingId =
                item.taxRateId && !knownRateIds.has(item.taxRateId) ? item.taxRateId : null;
              return (
                <div key={item.id} className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:items-center">
                  <Label className="text-sm">
                    {item.productName ?? `${item.productId?.slice(0, 8)}…`}
                  </Label>
                  <Select
                    value={value}
                    onValueChange={(next) =>
                      item.id && setSelections((s) => ({ ...s, [item.id!]: next }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>No tax</SelectItem>
                      {danglingId && (
                        <SelectItem value={danglingId}>
                          Missing rate ({danglingId.slice(0, 8)}…) — replace
                        </SelectItem>
                      )}
                      {taxRates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id!}>
                          {rate.code} — {rate.name} ({rate.rate}%)
                          {rate.source === "country" ? " · default" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
            {lineItems.length === 0 && (
              <p className="text-muted-foreground text-sm">This schedule has no line items.</p>
            )}
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting || changed.length === 0}>
              {submitting && <Spinner className="size-4" />}
              Save{changed.length > 0 ? ` (${changed.length})` : ""}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
